// Workflow variables: rendering helpers shared by the API (run prompts,
// decision prompts, approval context) and the web app (beslisstap contract
// §0/§6).

import type { WorkflowVariables } from '../types/decision.js'

/** Name rule for a workflow variable (a decision step's `variable`, keys of `set.vars`). */
export const WORKFLOW_VARIABLE_NAME_RE = /^[a-z][a-z0-9_]{0,31}$/
/** Value rule for a decision option (`value`). */
export const DECISION_OPTION_VALUE_RE = /^[a-z0-9_-]{1,32}$/
export const WORKFLOW_VARIABLE_VALUE_MAX = 200

const PLACEHOLDER_RE = /\{\{\s*vars\.([a-z][a-z0-9_]{0,31})\s*\}\}/g

/**
 * Replaces `{{vars.<name>}}` with the variable's value. An unknown variable
 * becomes `(not set)` so a prompt never shows raw template syntax.
 */
export function renderWorkflowVars(text: string, vars: WorkflowVariables | null | undefined): string {
  return text.replace(PLACEHOLDER_RE, (_match, name: string) => vars?.[name]?.value ?? '(not set)')
}

/** Names of every `{{vars.<name>}}` placeholder in `text` (for editor warnings). */
export function workflowVarPlaceholders(text: string | null | undefined): string[] {
  if (!text) return []
  return [...new Set([...text.matchAll(PLACEHOLDER_RE)].map((m) => m[1]!))]
}

/**
 * Body lines of a prompt's `## Workflow variables` section
 * (`- name = value — reason`), sorted by name; empty when there are none.
 */
export function renderVariablesSection(vars: WorkflowVariables | null | undefined): string[] {
  const entries = Object.entries(vars ?? {}).sort(([a], [b]) => a.localeCompare(b))
  return entries.map(([name, v]) => `- ${name} = ${v.value}${v.reason ? ` — ${oneLine(v.reason, 300)}` : ''}`)
}

/** `{ name: { value, reason } }` — the compact form `get_task` returns. */
export function compactWorkflowVars(vars: WorkflowVariables | null | undefined): Record<string, { value: string; reason: string | null }> {
  const out: Record<string, { value: string; reason: string | null }> = {}
  for (const [name, v] of Object.entries(vars ?? {})) out[name] = { value: v.value, reason: v.reason ?? null }
  return out
}

function oneLine(text: string, max: number): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat
}
